import React from 'react';
import { Rocket, BriefcaseBusiness, ArrowRight, ShieldCheck, TrendingUp, Building2, LineChart, Sparkles, BarChart3, CheckCircle } from 'lucide-react';

const SectionTitle = ({ subtitle, title, align = 'left' }) => (
  <div className={`mb-12 ${align === 'center' ? 'text-center' : ''}`}>
    <div className={`flex items-center gap-2 mb-3 ${align === 'center' ? 'justify-center' : ''}`}>
      <div className="w-12 h-0.5 bg-[#F49611]"></div>
      <span className="text-[#F49611] font-medium uppercase tracking-wider text-sm">{subtitle}</span>
    </div>
    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4">{title}</h2>
  </div>
);

const ServiceCard = ({ Icon, title, description, index }) => (
  <div className="group">
    <div className="relative p-8 rounded-xl bg-white border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <div className="absolute top-0 right-0 w-24 h-24 bg-[#E8EEF6] rounded-bl-full -z-10 group-hover:bg-[#F49611] transition-colors duration-300"></div>
      <div className="flex items-start gap-6">
        <div className="relative">
          <div className="w-12 h-12 rounded-xl bg-[#E8EEF6] group-hover:bg-[#F49611] transition-colors duration-300 flex items-center justify-center">
            <Icon className="w-6 h-6 text-[#113262] group-hover:text-white transition-colors duration-300" />
          </div>
          <span className="absolute -bottom-4 -right-4 text-4xl font-bold text-[#E8EEF6] group-hover:text-[#F49611]/10 transition-colors duration-300">
            {String(index).padStart(2, '0')}
          </span>
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-[#113262] mb-3">{title}</h3>
          <p className="text-gray-600">{description}</p>
        </div>
      </div>
    </div>
  </div>
);

const MainContent = () => {
  return (
    <div>
      {/* Intro Section */}
      <div className="max-w-6xl w-full mx-auto px-6 sm:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <SectionTitle 
              subtitle="Who We Are"
              title="Your Partner in Private Equity Investments"
            />
            <p className="text-lg text-gray-600 mb-8">
              With extensive expertise in the private equity landscape, we provide institutional-quality access to 
              high-potential private companies across various stages and sectors. Our team conducts rigorous due diligence 
              and monitors emerging trends to identify opportunities with exceptional growth potential and favorable risk-return profiles.
            </p>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <div className="text-3xl font-bold text-[#113262] mb-2">25+</div>
                <div className="text-gray-600">PE Partnerships</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#113262] mb-2">₹650Cr+</div>
                <div className="text-gray-600">PE Assets Advised</div>
              </div>
            </div>
          </div>
          <div className="bg-[#E8EEF6] rounded-2xl p-8 md:p-12">
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-[#113262] mb-6">Why Choose Us</h3>
              {[
                "Access to exclusive private equity opportunities",
                "Comprehensive due diligence and manager selection",
                "Strategic portfolio construction across PE stages",
                "Ongoing monitoring and performance analysis"
              ].map((point, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-6 h-6 rounded-full bg-[#F49611] flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-700">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="max-w-6xl w-full mx-auto px-6 sm:px-6 lg:px-8 py-6 md:py-6 lg:py-8">
        <SectionTitle 
          subtitle="Our Offerings"
          title="Comprehensive Private Equity Solutions"
        />
        <div className="grid md:grid-cols-2 gap-8">
          {[
            {
              icon: Rocket,
              title: "Early-Stage Investing",
              description: "Access to emerging technologies and innovative business models in sectors like EV, AI, and semiconductor development with high growth potential"
            },
            {
              icon: TrendingUp,
              title: "Mid-Stage Growth Capital",
              description: "Strategic investments in companies with proven concepts ready to scale, targeting established business models with clear growth trajectories"
            },
            {
              icon: Building2,
              title: "Late-Stage Pre-IPO Investments",
              description: "Opportunities in scaled businesses generating positive EBITDA seeking capital for acquisitions, market expansion, or technology development"
            },
            {
              icon: LineChart,
              title: "Investment Strategy Development",
              description: "Customized private equity allocation strategies aligned with your risk profile, investment objectives, and broader portfolio diversification goals"
            }
          ].map((service, index) => (
            <ServiceCard 
              key={index}
              Icon={service.icon}
              title={service.title}
              description={service.description}
              index={index + 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default MainContent;